import {Dialog as MuiDialog, Box, Typography, IconButton} from '@mui/material';
import type {QuestionType} from '@jobseeker/lotus/components/SurveyQuestion/types';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {Image} from '@jobseeker/lotus/components/Image';
import {CrossIcon, SurveyCheckGreen} from '@jobseeker/lotus/assets/icons';
import {Dispatch, SetStateAction, useState, useCallback} from 'react';
import surveyService from '@jobseeker/common-core/services/survey.service';
import Loader from '@jobseeker/lotus/components/Loader';

type SurveyPopupProps = {
  questions: QuestionType[];
  setShowSurvey: Dispatch<SetStateAction<boolean>>;
  successCallback: () => void;
  courseTitle?: string;
  instituteName?: string;
  courseLogoPath?: string;
  coursePublishedId?: number;
};

const DEFAULT_REASONS = [
  'To transition my Career',
  'Hone my skills',
  'To get a salary hike',
  'Growth in current role/organization',
  'Enhance my knowledge',
];

const SurveyPopup = ({
  questions,
  setShowSurvey,
  successCallback,
  courseTitle = '',
  instituteName = '',
  courseLogoPath = '',
  coursePublishedId,
}: SurveyPopupProps) => {
  const isDeviceLarge = useIsLargeDevice('md');
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [showValidation, setShowValidation] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const currQuestion = questions?.[0];

  // Get reason options from survey question options or use defaults
  let reasonOptions = DEFAULT_REASONS;
  try {
    if (currQuestion?.options) {
      const parsed = JSON.parse(currQuestion.options);
      if (parsed?.chips?.length) {
        reasonOptions = parsed.chips;
      }
    }
  } catch {
    // fallback to defaults
  }

  const handleClose = useCallback(() => {
    // https://infoedge.atlassian.net/browse/IIMHIRJS-1319?focusedCommentId=1463191
    // setShowSurvey(false);
    // successCallback();
  }, [setShowSurvey, successCallback]);

  const toggleReason = useCallback((reason: string) => {
    setSelectedReasons(prev => {
      if (prev.includes(reason)) {
        return prev.filter(r => r !== reason);
      }
      return [...prev, reason];
    });
    setShowValidation(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (selectedReasons.length === 0) {
      setShowValidation(true);
      return;
    }

    setLoading(true);

    try {
      if (currQuestion) {
        await surveyService.answerSurveyQuestion({
          source: `course-${coursePublishedId || 'unknown'}`,
          answer: JSON.stringify({
            chips: JSON.stringify(selectedReasons),
          }),
          questionId: currQuestion.id,
          surveyId: currQuestion.surveyId,
        });
      }

      setSubmitted(true);

      // Auto-close after 3 seconds and trigger success callback
      setTimeout(() => {
        setShowSurvey(false);
        successCallback();
      }, 3000);
    } catch (error) {
      console.error('Survey submission failed:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedReasons, currQuestion, setShowSurvey, successCallback]);

  const isSubmitDisabled = selectedReasons.length === 0;

  return (
    <MuiDialog
      open
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: '20px',
          maxWidth: 570,
          width: '100%',
          boxShadow: '0px 6.48px 6px rgba(41, 41, 41, 0.08)',
          m: isDeviceLarge ? 2 : 1,
          overflow: 'visible',
        },
      }}
      sx={{
        '.MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
        ...(!isDeviceLarge && {
          '.MuiDialog-paper': {
            position: 'absolute',
            bottom: 0,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            m: 0,
            maxHeight: '90vh',
          },
        }),
      }}
    >
      <Box sx={{p: 3, position: 'relative'}}>
        {submitted ? (
          /* ===== Thank You State ===== */
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={4}
            gap={2}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: '#E8F5E9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Image
                src={SurveyCheckGreen}
                alt="success"
                height={28}
                width={28}
              />
            </Box>
            <Typography
              fontSize={16}
              fontWeight={600}
              textAlign="center"
              color="#292929"
            >
              Thank you for your interest in this course
            </Typography>
            <Typography
              fontSize={13}
              fontWeight={400}
              textAlign="center"
              color="#666"
            >
              The course provider may reach out to you for further steps
            </Typography>
          </Box>
        ) : (
          /* ===== Interest Modal State ===== */
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Header */}
            <Typography
              fontSize={14}
              fontWeight={500}
              color="#292929"
              pr={3}
              lineHeight="20px"
            >
              Your success matters! Your reason to enquire for this course?
            </Typography>

            {/* Course Info Card */}
            {(courseTitle || instituteName) && (
              <Box
                sx={{
                  bgcolor: '#f5f7f6',
                  borderRadius: '12px',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                {courseLogoPath && (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      flexShrink: 0,
                      bgcolor: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Image
                      src={courseLogoPath}
                      alt={instituteName}
                      height={36}
                      width={36}
                    />
                  </Box>
                )}
                <Box>
                  {instituteName && (
                    <Typography
                      fontSize={12}
                      fontWeight={600}
                      color="#292929"
                      lineHeight="16px"
                    >
                      {instituteName}
                    </Typography>
                  )}
                  {courseTitle && (
                    <Typography
                      fontSize={11}
                      fontWeight={400}
                      color="#666"
                      lineHeight="15px"
                      mt={0.25}
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {courseTitle}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Reason Chips */}
            <Box display="flex" flexWrap="wrap" gap={1}>
              {reasonOptions.map(reason => {
                const isSelected = selectedReasons.includes(reason);
                return (
                  <Box
                    key={reason}
                    onClick={() => toggleReason(reason)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: '20px',
                      border: `1px solid ${isSelected ? '#292929' : '#D0D0D0'}`,
                      bgcolor: isSelected ? '#F5F5F5' : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#999',
                        bgcolor: '#FAFAFA',
                      },
                      userSelect: 'none',
                    }}
                  >
                    <Typography
                      fontSize={12}
                      fontWeight={400}
                      color="#292929"
                      lineHeight="16px"
                      whiteSpace="nowrap"
                    >
                      {reason}
                    </Typography>
                    <Typography
                      fontSize={14}
                      fontWeight={500}
                      color={isSelected ? '#292929' : '#999'}
                      lineHeight="16px"
                    >
                      {isSelected ? '✓' : '+'}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Validation message */}
            {showValidation && (
              <Typography fontSize={12} color="#D11124" lineHeight="16px">
                Select at least one option
              </Typography>
            )}

            {/* Submit Button */}
            <Box
              onClick={!isSubmitDisabled && !loading ? handleSubmit : undefined}
              sx={{
                bgcolor: isSubmitDisabled ? '#999' : '#292929',
                color: '#FFFFFF',
                borderRadius: '24px',
                py: 1,
                px: 4,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isSubmitDisabled ? 'not-allowed' : 'pointer',
                opacity: isSubmitDisabled ? 0.5 : 1,
                transition: 'all 0.2s ease',
                alignSelf: 'flex-start',
                minWidth: 100,
                '&:hover': {
                  bgcolor: isSubmitDisabled ? '#999' : '#3d3d3d',
                },
              }}
            >
              {loading ? (
                <Loader size={18} sx={{color: '#fff'}} />
              ) : (
                <Typography
                  fontSize={13}
                  fontWeight={500}
                  lineHeight="20px"
                  color="inherit"
                >
                  Submit
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </MuiDialog>
  );
};

export default SurveyPopup;
